import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainApiService from "../../../services/mainApi.service";
import {
  ISelectType,
  Modal,
  Table,
  useConfig,
  useFetch,
  useNotificationContext,
  UsePrivileges,
  useRole,
} from "@4uhub/lib4uhub";
import LockIcon from "@mui/icons-material/Lock";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import PinRoundedIcon from "@mui/icons-material/PinRounded";
import { useCallback, useMemo, useState } from "react";
import UsersResetModal from "./UsersResetModal";
import UsersResetSuccess from "./UsersResetSuccess";
import { UserTemporaryPasswordModal } from "./UserTemporaryPasswordModal";
import UserChangePinModal from "./UserChangePinModal";

import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { authService } from "../../../services/auth.service";
import { IGetAllUsers } from "../../../models/users";
import { Chip } from "@mui/material";
import { format, startOfDay, endOfDay } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import UserMacroFilters, { IFilterParams } from "./UserMacroFilters";
import { useInstalledExtensions } from "../../../hooks/useInstalledExtensions";
import { syncBeneficiary } from "../../../services/individualBeneficiaries.service";
import { getEstToken } from "../../../services/establishment.service";

const translationPath = "page.register.users.";

const USERS_ROUTE = process.env.REACT_APP_IDENTIY + "/api/v1/Users/";
const usersService = new MainApiService<IGetAllUsers>(USERS_ROUTE);

const UsersPage = () => {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const [openTemporaryPassword, setOpenTemporaryPassword] = useState(false);
  const [openChangePin, setOpenChangePin] = useState(false);
  const { setMessage } = useNotificationContext();
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IGetAllUsers>();
  const { privileges } = UsePrivileges();
  const isAdmin = privileges.some(
    (privilege) => privilege.domainName === "all",
  );
  const canUpdateUserPin = privileges.some(
    (privilege) =>
      privilege.domainName === "all" ||
      (privilege.domainName === "userpin" &&
        privilege.actions.includes("Update")),
  );

  const [hasBeneficiarySyncExtension] = useInstalledExtensions("26");

  const {
    establishments,
    configuration: {
      establishment: { id: defaultEstablishmentId },
    },
  } = useConfig();

  const { eI } = useParams();

  /**Estabelecimento da aba, derivado da url. Usado quando o token não possui o
   * id do estabelecimento gravado
   */
  const actualEstablishmentId = useMemo(
    () => establishments.find((e) => e.tenantSequential === Number(eI))?.id,
    [establishments, eI],
  );

  const { sendRequest: resendEmail, loading } = useFetch(
    authService.resendEmail,
  );

  const { sendRequest: syncBeneficiaryRequest, loading: syncLoading } =
    useFetch(syncBeneficiary);
  const [refresh, setRefresh] = useState(0);

  const today = useMemo(() => new Date(), []);

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    InitialDate: today,
    EndDate: today,
    Status: "",
  });

  const createPermission = useRole(["Create"]);

  const columns: GridColDef<IGetAllUsers>[] = [
    {
      field: "userName",
      headerName: t(translationPath + "username") || "Username",
    },
    {
      field: "fullName",
      headerName: t(translationPath + "full_name") || "Full name",
    },
    {
      field: "email",
      headerName: t(translationPath + "email") || "E-mail",
    },
    {
      field: "emailConfirmed",
      headerName:
        t(translationPath + "email_confirmed_label") || "E-mail status",
      maxWidth: 150,
      renderCell: (params) => {
        return (
          <Chip
            size="small"
            label={
              params.value
                ? t(translationPath + "email_confirmed_tag")
                : t(translationPath + "email_not_confirmed_tag")
            }
            color={params.value ? "success" : "error"}
          />
        );
      },
    },
    {
      field: "emailConfirmationDate",
      headerName:
        t(translationPath + "email_confirmation_date") ||
        "E-mail confirmation date",
      renderCell: (params) => {
        return params.row.emailConfirmationDate
          ? format(
              new Date(params.row.emailConfirmationDate.substring(0, 19)),
              "Pp",
              {
                locale: language === "pt-BR" ? ptBR : enUS,
              },
            ).replaceAll(",", "")
          : "-";
      },
    },
  ];

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleRowClick = (params: GridRowParams<IGetAllUsers>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const handleAdd = () => {
    navigate("new");
  };

  const onCloseHandler = () => setOpen(false);

  const onCloseModalHandler = () => setOpenModal(false);
  const onOpenModalHandler = () => setOpenModal(true);

  const onCloseTemporaryPasswordHandler = () => setOpenTemporaryPassword(false);
  const onOpenTemporaryPasswordHandler = () => setOpenTemporaryPassword(true);

  const onCloseChangePinHandler = () => setOpenChangePin(false);
  const onOpenChangePinHandler = () => setOpenChangePin(true);

  const onResendEmailConfirmation = useCallback(
    async (username: string) => {
      const { success } = await resendEmail({
        userName: username,
        softwareCode: "1",
      });
      if (success) {
        setMessage({
          message: `${t(
            "components.forgetPasswordForm.notification_email_sent",
          )}`,
          type: "success",
          notificationKey: new Date().getMilliseconds(),
        });
      }
    },
    [setMessage, resendEmail, t],
  );

  const onSyncBeneficiary = useCallback(
    async (document: string) => {
      if (!document) {
        setMessage({
          message: `${t(translationPath + "beneficiary_sync_no_document")}`,
          type: "error",
          notificationKey: new Date().getMilliseconds(),
        });
        return;
      }

      const { success } = await syncBeneficiaryRequest({
        establishmentId:
          getEstToken()?.establishmentId ||
          actualEstablishmentId ||
          defaultEstablishmentId,
        document,
      });

      if (success) {
        setMessage({
          message: `${t(translationPath + "beneficiary_sync_success")}`,
          type: "success",
          notificationKey: new Date().getMilliseconds(),
        });
      }
    },
    [
      setMessage,
      syncBeneficiaryRequest,
      actualEstablishmentId,
      defaultEstablishmentId,
      t,
    ],
  );

  const props = useMemo(() => {
    return {
      refresh,
      StartDate: filterParams.InitialDate
        ? startOfDay(filterParams.InitialDate).toISOString()
        : undefined,
      EndDate: filterParams.EndDate
        ? endOfDay(filterParams.EndDate).toISOString()
        : undefined,
      Status:
        filterParams.Status === "true"
          ? true
          : filterParams.Status === "false"
            ? false
            : undefined,
    };
  }, [filterParams, refresh]);

  return (
    <>
      <Modal open={openModal} onClose={onCloseModalHandler} maxDialogWidth="sm">
        <UsersResetSuccess email={selectedUser ? selectedUser.email : ""} />
      </Modal>
      <Modal open={open} onClose={onCloseHandler} maxDialogWidth="sm">
        <UsersResetModal
          user={selectedUser!}
          onCancel={onCloseHandler}
          onCloseHandler={onCloseHandler}
          onOpenHandler={onOpenModalHandler}
        />
      </Modal>
      <Modal
        open={openTemporaryPassword}
        onClose={onCloseTemporaryPasswordHandler}
        maxDialogWidth="sm"
      >
        <UserTemporaryPasswordModal user={selectedUser!} />
      </Modal>
      <Modal
        open={openChangePin}
        onClose={onCloseChangePinHandler}
        maxDialogWidth="sm"
      >
        <UserChangePinModal
          user={selectedUser!}
          onClose={onCloseChangePinHandler}
        />
      </Modal>
      <Table<IGetAllUsers>
        showDefaultMacroFilters={false}
        service={usersService}
        columns={columns}
        title={t(translationPath + "users") || "System users"}
        searchInput
        onAdd={handleAdd}
        showDeleteButton={false}
        showButton={createPermission}
        onRowClick={handleRowClick}
        showDelete={false}
        syncing={loading || syncLoading}
        serviceProps={props}
        macroFilters={() => (
          <UserMacroFilters
            filterParams={filterParams}
            onFilterParams={handleFilterChange}
          />
        )}
        searchInputPlaceHolder={
          t(translationPath + "search_user") || "Searh for a user"
        }
        addButtonLabel={t(translationPath + "add_user") || "Add User"}
        rowActions={(params) => [
          {
            action: (params) => {
              setSelectedUser(params.row);
              setOpen(true);
            },
            icon: <LockIcon fontSize="small" />,
            text: `${t(translationPath + "reset_password.button")}`,
          },
          isAdmin
            ? {
                action: (params) => {
                  setSelectedUser(params.row);
                  onOpenTemporaryPasswordHandler();
                },
                icon: <LockClockRoundedIcon fontSize="small" />,
                text: `${t(translationPath + "temporary_password.button")}`,
              }
            : null,
          params.row.emailConfirmed
            ? null
            : {
                action: (params) => {
                  onResendEmailConfirmation(params.row.userName);
                },
                icon: <MarkEmailReadIcon fontSize="small" />,
                text: `${t(translationPath + "resend_mail")}`,
              },
          hasBeneficiarySyncExtension
            ? {
                action: (params) => {
                  setSelectedUser(params.row);
                  onSyncBeneficiary(params.row.userName);
                },
                icon: <SyncRoundedIcon fontSize="small" />,
                text: `${t(translationPath + "beneficiary_sync")}`,
              }
            : null,
          canUpdateUserPin
            ? {
                action: (params) => {
                  setSelectedUser(params.row);
                  onOpenChangePinHandler();
                },
                icon: <PinRoundedIcon fontSize="small" />,
                text: `${t(translationPath + "change_pin.button")}`,
              }
            : null,
        ]}
        confirmOptions={{
          title: t(translationPath + "confirm.title"),
          description: t(translationPath + "confirm.description"),
          confirmationButtonProps: {
            variant: "contained",
          },
        }}
      />
    </>
  );
};

export default UsersPage;
